// frontend/src/pages/BatchCsvTab.jsx
import React, { useRef } from "react";
import PropTypes from "prop-types";

function fmtSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Count data rows in a CSV File (excludes header, blank lines).
 * Returns null for non-CSV files (e.g. .xlsx) where text parsing is invalid.
 *
 * @param {File} file
 * @returns {Promise<number|null>}
 */
export async function countCsvRows(file) {
  const name = file.name ?? "";
  if (name.toLowerCase().endsWith(".xlsx")) return null;
  const text = await file.text();
  const lines = text.split("\n").filter((l) => l.trim().length > 0);
  return Math.max(0, lines.length - 1);
}

const IMPORT_MODES = [
  { value: "create", label: "Create new codes" },
  { value: "upsert", label: "Create or update (upsert by name)" },
];

/**
 * BatchCsvTab — file picker with filename/size preview, QR-type selector,
 * and import-mode selector.
 */
function BatchCsvTab({
  file,
  rowCount,
  qrType,
  onTypeChange,
  types,
  onFileChange,
  mode,
  onModeChange,
}) {
  const inputRef = useRef(null);

  const handleChange = async (e) => {
    const chosen = e.target.files?.[0] ?? null;
    if (!chosen) return;
    const count = await countCsvRows(chosen);
    onFileChange(chosen, count);
  };

  return (
    <div className="space-y-4">
      {/* QR type selector */}
      <div>
        <label
          className="block text-sm font-medium text-gray-700 mb-1"
          htmlFor="csv-qr-type"
        >
          QR type to import
        </label>
        <select
          id="csv-qr-type"
          value={qrType}
          onChange={(e) => onTypeChange(e.target.value)}
          className="w-full max-w-xs rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {types.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
      </div>

      {/* Import mode selector */}
      <div>
        <label
          className="block text-sm font-medium text-gray-700 mb-1"
          htmlFor="csv-import-mode"
        >
          Import mode
        </label>
        <select
          id="csv-import-mode"
          value={mode}
          onChange={(e) => onModeChange(e.target.value)}
          className="w-full max-w-xs rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {IMPORT_MODES.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>
      </div>

      <p className="text-sm text-gray-500">
        Upload a{" "}
        <code className="font-mono bg-gray-100 px-1 rounded">.csv</code> or{" "}
        <code className="font-mono bg-gray-100 px-1 rounded">.xlsx</code> file
        with the columns for the selected type — download the template to see
        them. The header row is optional and the{" "}
        <code className="font-mono bg-gray-100 px-1 rounded">name</code> column
        may be blank. Max 10 MB.
      </p>

      <div
        className="flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 px-6 py-10 cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => e.key === "Enter" && inputRef.current?.click()}
        role="button"
        tabIndex={0}
        aria-label="Choose CSV or Excel file"
      >
        <svg
          className="w-10 h-10 text-gray-400"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        <span className="text-sm text-gray-500">
          {file ? "Click to change file" : "Click to choose a CSV or Excel file"}
        </span>
        <input
          ref={inputRef}
          type="file"
          accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="hidden"
          onChange={handleChange}
        />
      </div>

      {/* File preview */}
      {file && (
        <div className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm">
          <svg
            className="w-5 h-5 text-green-600 shrink-0"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div>
            <span className="font-medium text-green-800">{file.name}</span>
            <span className="ml-2 text-green-700">
              — {fmtSize(file.size)}
              {rowCount !== null && (
                <>, {rowCount} data row{rowCount !== 1 ? "s" : ""} detected</>
              )}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

BatchCsvTab.propTypes = {
  file: PropTypes.instanceOf(File),
  rowCount: PropTypes.number,
  qrType: PropTypes.string.isRequired,
  onTypeChange: PropTypes.func.isRequired,
  types: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string, label: PropTypes.string }),
  ).isRequired,
  onFileChange: PropTypes.func.isRequired,
  mode: PropTypes.string.isRequired,
  onModeChange: PropTypes.func.isRequired,
};

BatchCsvTab.defaultProps = {
  file: null,
  rowCount: null,
};

export default BatchCsvTab;
